import { type AppEdge, edgeFormTypes } from '@/edges';
import { type AppNode, nodeFormTypes } from '@/nodes';
import { edgesState, nodesState, selectedEdgeState, selectedFlowIdState, selectedNodeState } from '@/state';
import { useUnwrappedAtom } from '@/state/loadableHooks';
import type { AppEdgeType, AppNodeType } from '@rock/types';
import { useUpdateNodeInternals } from '@xyflow/react';
import { useAtomValue } from 'jotai';
import { type FC, type ReactElement, startTransition, useCallback } from 'react';
import { toast } from 'sonner';
import type { AppFlowParams } from '@rock/types';

const ActiveSelectionPanel: FC<{ params: AppFlowParams; noSelectionContent: () => ReactElement }> = ({
    params,
    noSelectionContent,
}) => {
    const [selectedNode, setSelectedNode] = useUnwrappedAtom(selectedNodeState(params));
    const [selectedEdge, setSelectedEdge] = useUnwrappedAtom(selectedEdgeState(params));
    const [nodes = [], setNodes] = useUnwrappedAtom(nodesState(params));
    const [edges = [], setEdges] = useUnwrappedAtom(edgesState(params));
    const flowId = useAtomValue(selectedFlowIdState(params));
    const updatedNodeInternals = useUpdateNodeInternals();

    const updateNode = useCallback(
        async (updatedNode: AppNode) => {
            startTransition(async () => {
                await setNodes(nodes.map((n) => (n.id === updatedNode.id ? updatedNode : n)));
                await setSelectedNode(undefined);
                updatedNodeInternals(updatedNode.id);
                toast.success(`Node ${updatedNode.id} Updated!`);
            });
        },
        [nodes, setNodes, setSelectedNode, updatedNodeInternals],
    );

    const cancelUpdate = useCallback(async () => {
        startTransition(async () => {
            await setSelectedNode(undefined);
            await setSelectedEdge(undefined);
        });
    }, [setSelectedNode, setSelectedEdge]);

    const updateEdge = useCallback(
        async (updatedEdge: AppEdge) => {
            startTransition(async () => {
                await setEdges(edges.map((e) => (e.id === updatedEdge.id ? updatedEdge : e)));
                await setSelectedEdge(undefined);
            });
        },
        [edges, setEdges, setSelectedEdge],
    );

    const NodeFormCmp = selectedNode ? nodeFormTypes[selectedNode.type as AppNodeType] : undefined;
    const EdgeFormCmp = selectedEdge ? edgeFormTypes[selectedEdge.type as AppEdgeType] : undefined;

    if (!NodeFormCmp && !EdgeFormCmp) {
        return noSelectionContent();
    }

    return (
        <div className="flex flex-1 flex-col gap-3 overflow-hidden">
            <h4 className="text-md text-center">
                {selectedNode ? selectedNode.id : selectedEdge ? selectedEdge.id : ''}
            </h4>
            {NodeFormCmp && (
                <NodeFormCmp
                    key={selectedNode?.id}
                    spaceId={params.spaceId}
                    flowId={flowId}
                    node={selectedNode}
                    updateNode={updateNode}
                    cancelUpdate={cancelUpdate}
                />
            )}
            {!NodeFormCmp && EdgeFormCmp && (
                <EdgeFormCmp
                    key={selectedEdge?.id}
                    spaceId={params.spaceId}
                    flowId={flowId}
                    edge={selectedEdge}
                    updateEdge={updateEdge}
                    cancelUpdate={cancelUpdate}
                />
            )}
        </div>
    );
};

export default ActiveSelectionPanel;
